/**
 * Contractor Insurance Quote Wizard — Version H2
 * Pattern-matched to Motorcycle_QuoteWizard_H2:
 *  - DualViewWizard with progress + section success visuals
 *  - Toggles gate section "success" (requirements & vehicles)
 *  - Externalized ReactNode copy (contractor-copy.tsx)
 *  - DEMO_MODE (with optional UI) & TEST_MODE
 *  - Strings-only payload to backend
 */

import React, { useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, ProgressBar, Row } from "react-bootstrap";
import { CONTRACTOR_COPY, IContractorCopy } from "./Contractor-insurance-quote-v01A-example-strings";

/* ============================================================
   CONFIG
============================================================ */
const VERSION = "Contractor-H2";
const SHOW_VERSION = true;

const TEST_MODE = false; // true ⇒ logs payload, skips network
const API_GATEWAY_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";

// DEMO controls (independent of TEST_MODE)
const DEMO_MODE_DEFAULT = true;     // initial demo state (pre-populate)
const DEMO_MODE_UI_ENABLED = true;  // show a UI checkbox to toggle demo mode

// UI helpers
const clampPct = (p: number) => Math.min(99, Math.max(0, Math.round(p)));

/* ============================================================
   TYPES
============================================================ */
type ViewMode = "wizard" | "vertical";
export type FormShape = Record<string, unknown>;
export type SetForm = (patch: FormShape) => void;

export interface IWizardSection {
  id: string;
  title: React.ReactNode;
  explanation: React.ReactNode;
  enabled?: boolean;
  render: (form: FormShape, setForm: SetForm) => React.ReactNode;
}

/* ============================================================
   HELPERS
============================================================ */
const S = (v: unknown): string | null => {
  if (v == null) return null;
  if (typeof v === "string") {
    const t = v.trim();
    return t === "" ? null : t;
  }
  return String(v);
};
const hasVal = (v: unknown): boolean => {
  if (v == null) return false;
  if (typeof v === "string") return v.trim().length > 0;
  return true;
};

/* ============================================================
   COMPLETION SCHEMA
============================================================ */
type SectionKeys = {
  id: string;
  required: (form: FormShape) => string[];
  optional?: (form: FormShape) => string[];
};

const completionSchema: SectionKeys[] = [
  {
    id: "personal-info",
    required: () => ["contactFirst", "contactLast", "email", "phone"],
  },
  {
    id: "business",
    required: () => ["businessName", "fein", "yearsInBusiness", "employeeCount", "tradeType"],
  },
  {
    id: "requirements",
    required: (form) => {
      const need = ((form.needRequirements as string) ?? "no") === "yes";
      return need
        ? ["additionalInsured", "waiverOfSubrogation", "limitsPerOccurrence", "limitsAggregate"]
        : [];
    },
  },
  {
    id: "vehicles",
    required: (form) => {
      const own = ((form.ownFleet as string) ?? "no") === "yes";
      if (!hasVal(form.ownFleet)) return ["ownFleet"];
      if (!own) return ["ownFleet"];
      return ["ownFleet", "vehicleCount", "primaryVehicleType", "radiusMiles"];
    },
  },
  {
    id: "coverage",
    required: () => [],
    optional: () => ["coverageType", "glDeductible"],
  },
  {
    id: "remarks",
    required: () => [],
    optional: () => ["remarks"],
  },
];

const requiredKeysForSection = (sectionId: string, form: FormShape): string[] =>
  completionSchema.find((s) => s.id === sectionId)?.required(form) ?? [];

const optionalKeysForSection = (sectionId: string, form: FormShape): string[] =>
  completionSchema.find((s) => s.id === sectionId)?.optional?.(form) ?? [];

const sectionIsComplete = (sectionId: string, form: FormShape): boolean => {
  const req = requiredKeysForSection(sectionId, form);
  if (req.length > 0) return req.every((k) => hasVal(form[k]));
  const opt = optionalKeysForSection(sectionId, form);
  return opt.length > 0 && opt.some((k) => hasVal(form[k]));
};

const overallCompletion = (enabledSectionIds: string[], form: FormShape): number => {
  const allReq: string[] = enabledSectionIds.flatMap((id) => requiredKeysForSection(id, form));
  const uniq = Array.from(new Set(allReq));
  if (uniq.length === 0) return 0;
  const filled = uniq.reduce((acc, k) => (hasVal(form[k]) ? acc + 1 : acc), 0);
  return (filled / uniq.length) * 100;
};

/* ============================================================
   Controls
============================================================ */
const WizardControls: React.FC<{
  onPrev: () => void;
  onNext: () => void;
  onSubmit: () => void;
  isFirst: boolean;
  isLast: boolean;
  submitting?: boolean;
  copy?: IContractorCopy;
}> = ({ onPrev, onNext, onSubmit, isFirst, isLast, submitting, copy = CONTRACTOR_COPY }) => (
  <div className="d-flex justify-content-between">
    <Button variant="secondary" onClick={onPrev} disabled={isFirst || !!submitting}>
      {copy.buttons.previous}
    </Button>
    {isLast ? (
      <Button variant="success" onClick={onSubmit} disabled={!!submitting}>
        {submitting ? "Processing..." : copy.buttons.submit}
      </Button>
    ) : (
      <Button variant="primary" onClick={onNext} disabled={!!submitting}>
        {copy.buttons.next}
      </Button>
    )}
  </div>
);

/* ============================================================
   Dual View Wizard — Contractor H2
   (Requirements & Vehicles sections only show success when toggles are ON)
============================================================ */
export const DualViewWizard_Contractor_H2: React.FC<{
  mode?: ViewMode;
  sections: IWizardSection[];
  initialForm?: FormShape;
  className?: string;
  onSubmit?: (form: FormShape) => void;
  submitting?: boolean;
  submitted?: boolean;
  copy?: IContractorCopy;
}> = ({
  mode = "wizard",
  sections,
  initialForm = {},
  className,
  onSubmit,
  submitting = false,
  submitted = false,
  copy = CONTRACTOR_COPY,
}) => {
  const enabledSections = useMemo(
    () => sections.filter((s) => s.enabled !== false),
    [sections]
  );

  const [form, setFormState] = useState<FormShape>(initialForm);
  const setForm: SetForm = (patch) => setFormState((prev) => ({ ...prev, ...patch }));

  const [current, setCurrent] = useState(0);
  const [activeId, setActiveId] = useState<string>(enabledSections[0]?.id ?? "");

  const ids = useMemo(() => enabledSections.map((s) => s.id), [enabledSections]);
  const activeIndex = Math.max(0, ids.indexOf(activeId));
  const total = Math.max(1, enabledSections.length);
  const enabledIds = enabledSections.map((s) => s.id);

  const rawPct = overallCompletion(enabledIds, form);
  const pctLabel = submitted ? 100 : clampPct(rawPct);
  const pct = submitted ? 100 : clampPct(rawPct);

  // Visual success override rules (like Motorcycle H2):
  // - requirements: only success if needRequirements === "yes" AND sectionIsComplete
  // - vehicles: only success if ownFleet === "yes" AND sectionIsComplete
  const sectionShowsSuccess = (sectionId: string): boolean => {
    const isComplete = sectionIsComplete(sectionId, form);
    if (sectionId === "requirements") {
      const need = ((form.needRequirements as string) ?? "no") === "yes";
      return need && isComplete;
    }
    if (sectionId === "vehicles") {
      const own = ((form.ownFleet as string) ?? "no") === "yes";
      return own && isComplete;
    }
    return isComplete;
  };

  if (mode === "vertical") {
    return (
      <div className={className}>
        <Card className="position-sticky mb-3" style={{ top: 16, zIndex: 1 }}>
          <Card.Header>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <strong>
                  {copy.appTitle} {SHOW_VERSION ? `— ${VERSION}` : ""} · Section {activeIndex + 1} of {total}
                </strong>{" "}
                — {enabledSections[activeIndex]?.title}
              </div>
              <div style={{ minWidth: 180 }}>
                <ProgressBar now={pct} label={`${pctLabel}%`} />
              </div>
            </div>
          </Card.Header>
        </Card>

        {enabledSections.map((s) => {
          const isActive = activeId === s.id;
          const successVisual = sectionShowsSuccess(s.id);

          const cardClasses = [
            "border",
            successVisual ? "border-success bg-success-subtle" : "",
            isActive && !successVisual ? "border-primary" : "",
          ].join(" ").trim();

          const headerClasses = isActive ? "bg-primary text-white" : "";

          return (
            <Row key={s.id} className="mb-3" style={{ scrollMarginTop: 90 }}>
              <Col
                md={8}
                onClick={() => setActiveId(s.id)}
                onFocusCapture={() => setActiveId(s.id)}
              >
                <Card className={cardClasses}>
                  <Card.Header className={headerClasses}>
                    {s.title}
                    {successVisual && !isActive && <Badge bg="success" className="ms-2">{copy.buttons.completeBadge}</Badge>}
                    {successVisual && isActive && <Badge bg="light" text="dark" className="ms-2">{copy.buttons.completeBadge}</Badge>}
                  </Card.Header>
                  <Card.Body>{s.render(form, setForm)}</Card.Body>
                </Card>
              </Col>

              <Col md={4}>
                <Card className={isActive ? "border border-primary" : "border"}>
                  <Card.Header className={isActive ? "bg-primary text-white" : ""}>
                    {copy.sectionHelpHeader}
                  </Card.Header>
                  <Card.Body>
                    <strong>{s.title}</strong>
                    <p className="mb-0">{s.explanation}</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          );
        })}

        <div className="d-flex justify-content-end mt-3">
          <Button variant="success" onClick={() => onSubmit?.(form)} disabled={!!submitting}>
            {submitting ? "Processing..." : copy.buttons.submit}
          </Button>
        </div>
      </div>
    );
  }

  const step = enabledSections[current];
  const successVisualForStep = sectionShowsSuccess(step.id);
  const cardClasses = successVisualForStep ? "border border-success bg-success-subtle" : "";

  return (
    <Card className={`${className ?? ""} ${cardClasses}`}>
      <Card.Header>
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <strong>
              {copy.appTitle} {SHOW_VERSION ? `— ${VERSION}` : ""} · Step {current + 1} of {total}
            </strong>{" "}
            — {step.title}
          </div>
          <div style={{ minWidth: 160 }}>
            <ProgressBar now={pct} label={`${pctLabel}%`} />
          </div>
        </div>
      </Card.Header>

      <Card.Body>
        {step.render(form, setForm)}

        <Card className="mt-3">
          <Card.Header>{copy.sectionHelpHeader}</Card.Header>
          <Card.Body>
            <strong>{step.title}</strong>
            <p className="mb-0">{step.explanation}</p>
          </Card.Body>
        </Card>

        <hr />
        <WizardControls
          onPrev={() => setCurrent((c) => Math.max(0, c - 1))}
          onNext={() => setCurrent((c) => Math.min(total - 1, c + 1))}
          onSubmit={() => onSubmit?.(form)}
          isFirst={current === 0}
          isLast={current === total - 1}
          submitting={submitting}
          copy={copy}
        />
      </Card.Body>
    </Card>
  );
};

/* ============================================================
   Example Usage — Contractor Quote H2
============================================================ */
export const Contractor_QuoteWizard_H2_A: React.FC<{ copy?: IContractorCopy }> = ({ copy = CONTRACTOR_COPY }) => {
  const [uiMode, setUiMode] = useState<ViewMode>("vertical");
  const [demoMode, setDemoMode] = useState<boolean>(DEMO_MODE_DEFAULT);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Defaults
  const initialBase: FormShape = { ownFleet: "no", needRequirements: "no" };

  // Demo data
  const DEMO_DATA: FormShape = {
    ...initialBase,
    // Personal
    contactFirst: "Alex",
    contactLast: "Builder",
    email: "alex.builder@example.com",
    phone: "(555) 555-0123",

    // Business
    businessName: "Acme Contracting LLC",
    fein: "123456789",
    yearsInBusiness: "7",
    employeeCount: "12",
    tradeType: "Electrical",

    // Requirements (off by default)
    needRequirements: "yes",
    additionalInsured: "perProject",
    waiverOfSubrogation: "yes",
    limitsPerOccurrence: "1000000",
    limitsAggregate: "2000000",
    certHolders: "Owner A, GC B",

    // Vehicles (off by default)
    ownFleet: "yes",
    vehicleCount: "3",
    primaryVehicleType: "pickups",
    radiusMiles: "50",

    // Coverage
    coverageType: "fullPackage",
    glDeductible: "500",

    // Remarks
    remarks: "Mostly commercial TI work; contracts up to $250k; OSHA 30 certified leads.",
  };

  // Sections
  const sections: IWizardSection[] = useMemo(() => {
    const personal: IWizardSection = {
      id: "personal-info",
      title: copy.personal.title,
      explanation: copy.personal.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="contactFirst">
                <Form.Label>{copy.personal.fields.contactFirst.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.contactFirst as string) ?? ""}
                  onChange={(e) => setForm({ contactFirst: e.target.value })}
                  placeholder={copy.personal.fields.contactFirst.placeholder}
                  isValid={hasVal(form.contactFirst)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="contactLast">
                <Form.Label>{copy.personal.fields.contactLast.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.contactLast as string) ?? ""}
                  onChange={(e) => setForm({ contactLast: e.target.value })}
                  placeholder={copy.personal.fields.contactLast.placeholder}
                  isValid={hasVal(form.contactLast)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="email">
                <Form.Label>{copy.personal.fields.email.label}</Form.Label>
                <Form.Control
                  type="email"
                  value={(form.email as string) ?? ""}
                  onChange={(e) => setForm({ email: e.target.value })}
                  placeholder={copy.personal.fields.email.placeholder}
                  isValid={hasVal(form.email)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="phone">
                <Form.Label>{copy.personal.fields.phone.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.phone as string) ?? ""}
                  onChange={(e) => setForm({ phone: e.target.value })}
                  placeholder={copy.personal.fields.phone.placeholder}
                  inputMode="tel"
                  isValid={hasVal(form.phone)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    const business: IWizardSection = {
      id: "business",
      title: copy.business.title,
      explanation: copy.business.help,
      enabled: true,
      render: (form, setForm) => {
        const feinRaw = ((form.fein as string) ?? "").replace(/\D+/g, "");
        const feinLen = feinRaw.length;
        const feinInvalid = feinLen > 0 && feinLen !== 9;

        return (
          <Form>
            <Row className="g-3">
              <Col md={12}>
                <Form.Group controlId="businessName">
                  <Form.Label>{copy.business.fields.businessName.label}</Form.Label>
                  <Form.Control
                    type="text"
                    value={(form.businessName as string) ?? ""}
                    onChange={(e) => setForm({ businessName: e.target.value })}
                    placeholder={copy.business.fields.businessName.placeholder}
                    isValid={hasVal(form.businessName)}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="fein">
                  <div className="d-flex justify-content-between align-items-end">
                    <Form.Label className="mb-0">{copy.business.fields.fein.label}</Form.Label>
                    <small className={feinInvalid ? "text-danger" : "text-muted"}>
                      {copy.business.feinLengthPrefix}: {feinLen}/9{" "}
                      {feinInvalid ? "— " + (typeof copy.business.feinInvalid === "string" ? copy.business.feinInvalid : "FEIN must be 9 digits.") : ""}
                    </small>
                  </div>
                  <Form.Control
                    type="text"
                    value={(form.fein as string) ?? ""}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D+/g, "");
                      setForm({ fein: digits });
                    }}
                    placeholder={copy.business.fields.fein.placeholder}
                    isInvalid={feinInvalid}
                    isValid={!feinInvalid && feinLen === 9}
                  />
                  {feinInvalid && (
                    <Form.Control.Feedback type="invalid">
                      {copy.business.feinInvalid}
                    </Form.Control.Feedback>
                  )}
                  <Form.Text muted>{copy.business.feinTip}</Form.Text>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group controlId="yearsInBusiness">
                  <Form.Label>{copy.business.fields.yearsInBusiness.label}</Form.Label>
                  <Form.Control
                    type="text"
                    value={(form.yearsInBusiness as string) ?? ""}
                    onChange={(e) => setForm({ yearsInBusiness: e.target.value })}
                    placeholder={copy.business.fields.yearsInBusiness.placeholder}
                    inputMode="numeric"
                    isValid={hasVal(form.yearsInBusiness)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="employeeCount">
                  <Form.Label>{copy.business.fields.employeeCount.label}</Form.Label>
                  <Form.Control
                    type="text"
                    value={(form.employeeCount as string) ?? ""}
                    onChange={(e) => setForm({ employeeCount: e.target.value })}
                    placeholder={copy.business.fields.employeeCount.placeholder}
                    inputMode="numeric"
                    isValid={hasVal(form.employeeCount)}
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group controlId="tradeType">
                  <Form.Label>{copy.business.fields.tradeType.label}</Form.Label>
                  <Form.Control
                    type="text"
                    value={(form.tradeType as string) ?? ""}
                    onChange={(e) => setForm({ tradeType: e.target.value })}
                    placeholder={copy.business.fields.tradeType.placeholder}
                    isValid={hasVal(form.tradeType)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        );
      },
    };

    const requirements: IWizardSection = {
      id: "requirements",
      title: copy.requirements.title,
      explanation: copy.requirements.help,
      enabled: true,
      render: (form, setForm) => {
        const need = ((form.needRequirements as string) ?? "no") === "yes";
        const ai = (form.additionalInsured as string) ?? "";

        return (
          <Form>
            <Row className="g-3">
              <Col md={12}>
                <Form.Check
                  type="checkbox"
                  id="needRequirements"
                  label={copy.requirements.needReqCheckbox}
                  checked={need}
                  onChange={(e) => setForm({ needRequirements: e.target.checked ? "yes" : "no" })}
                />
              </Col>

              {need && (
                <>
                  <Col md={6}>
                    <Form.Group controlId="additionalInsured">
                      <Form.Label>{copy.requirements.fields.additionalInsured.label}</Form.Label>
                      <Form.Select
                        value={ai}
                        onChange={(e) => setForm({ additionalInsured: e.target.value })}
                        isValid={hasVal(ai)}
                      >
                        <option value="">{copy.requirements.fields.additionalInsured.placeholder}</option>
                        <option value="none">{copy.requirements.fields.additionalInsured.options.none}</option>
                        <option value="perProject">{copy.requirements.fields.additionalInsured.options.perProject}</option>
                        <option value="blanket">{copy.requirements.fields.additionalInsured.options.blanket}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="waiverOfSubrogation">
                      <Form.Label>{copy.requirements.fields.waiverOfSubrogation.label}</Form.Label>
                      <Form.Select
                        value={(form.waiverOfSubrogation as string) ?? ""}
                        onChange={(e) => setForm({ waiverOfSubrogation: e.target.value })}
                        isValid={hasVal(form.waiverOfSubrogation)}
                      >
                        <option value="">{copy.requirements.fields.waiverOfSubrogation.placeholder}</option>
                        <option value="yes">{copy.requirements.fields.waiverOfSubrogation.options.yes}</option>
                        <option value="no">{copy.requirements.fields.waiverOfSubrogation.options.no}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="limitsPerOccurrence">
                      <Form.Label>{copy.requirements.fields.limitsPerOccurrence.label}</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.limitsPerOccurrence as string) ?? ""}
                        onChange={(e) => setForm({ limitsPerOccurrence: e.target.value })}
                        placeholder={copy.requirements.fields.limitsPerOccurrence.placeholder}
                        inputMode="numeric"
                        isValid={hasVal(form.limitsPerOccurrence)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="limitsAggregate">
                      <Form.Label>{copy.requirements.fields.limitsAggregate.label}</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.limitsAggregate as string) ?? ""}
                        onChange={(e) => setForm({ limitsAggregate: e.target.value })}
                        placeholder={copy.requirements.fields.limitsAggregate.placeholder}
                        inputMode="numeric"
                        isValid={hasVal(form.limitsAggregate)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group controlId="certHolders">
                      <Form.Label>{copy.requirements.fields.certHolders.label}</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.certHolders as string) ?? ""}
                        onChange={(e) => setForm({ certHolders: e.target.value })}
                        placeholder={copy.requirements.fields.certHolders.placeholder}
                        isValid={hasVal(form.certHolders)}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>
          </Form>
        );
      },
    };

    const vehicles: IWizardSection = {
      id: "vehicles",
      title: copy.vehicles.title,
      explanation: copy.vehicles.help,
      enabled: true,
      render: (form, setForm) => {
        const own = ((form.ownFleet as string) ?? "no") === "yes";
        return (
          <Form>
            <Row className="g-3">
              <Col md={12}>
                <Form.Check
                  type="checkbox"
                  id="ownFleet"
                  label={copy.vehicles.ownFleetCheckbox}
                  checked={own}
                  onChange={(e) => setForm({ ownFleet: e.target.checked ? "yes" : "no" })}
                />
              </Col>

              {own && (
                <>
                  <Col md={4}>
                    <Form.Group controlId="vehicleCount">
                      <Form.Label>{copy.vehicles.fields.vehicleCount.label}</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.vehicleCount as string) ?? ""}
                        onChange={(e) => setForm({ vehicleCount: e.target.value })}
                        placeholder={copy.vehicles.fields.vehicleCount.placeholder}
                        inputMode="numeric"
                        isValid={hasVal(form.vehicleCount)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group controlId="primaryVehicleType">
                      <Form.Label>{copy.vehicles.fields.primaryVehicleType.label}</Form.Label>
                      <Form.Select
                        value={(form.primaryVehicleType as string) ?? ""}
                        onChange={(e) => setForm({ primaryVehicleType: e.target.value })}
                        isValid={hasVal(form.primaryVehicleType)}
                      >
                        <option value="">{copy.vehicles.fields.primaryVehicleType.placeholder}</option>
                        <option value="pickups">{copy.vehicles.fields.primaryVehicleType.options.pickups}</option>
                        <option value="vans">{copy.vehicles.fields.primaryVehicleType.options.vans}</option>
                        <option value="boxTrucks">{copy.vehicles.fields.primaryVehicleType.options.boxTrucks}</option>
                        <option value="trailers">{copy.vehicles.fields.primaryVehicleType.options.trailers}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group controlId="radiusMiles">
                      <Form.Label>{copy.vehicles.fields.radiusMiles.label}</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.radiusMiles as string) ?? ""}
                        onChange={(e) => setForm({ radiusMiles: e.target.value })}
                        placeholder={copy.vehicles.fields.radiusMiles.placeholder}
                        inputMode="numeric"
                        isValid={hasVal(form.radiusMiles)}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>
          </Form>
        );
      },
    };

    const coverage: IWizardSection = {
      id: "coverage",
      title: copy.coverage.title,
      explanation: copy.coverage.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="coverageType">
                <Form.Label>{copy.coverage.fields.coverageType.label}</Form.Label>
                <Form.Select
                  value={(form.coverageType as string) ?? ""}
                  onChange={(e) => setForm({ coverageType: e.target.value })}
                  isValid={hasVal(form.coverageType)}
                >
                  <option value="">{copy.coverage.fields.coverageType.placeholder}</option>
                  <option value="glOnly">{copy.coverage.fields.coverageType.options.glOnly}</option>
                  <option value="glAndAuto">{copy.coverage.fields.coverageType.options.glAndAuto}</option>
                  <option value="fullPackage">{copy.coverage.fields.coverageType.options.fullPackage}</option>
                  <option value="custom">{copy.coverage.fields.coverageType.options.custom}</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="glDeductible">
                <Form.Label>{copy.coverage.fields.glDeductible.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.glDeductible as string) ?? ""}
                  onChange={(e) => setForm({ glDeductible: e.target.value })}
                  placeholder={copy.coverage.fields.glDeductible.placeholder}
                  inputMode="numeric"
                  isValid={hasVal(form.glDeductible)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    const remarks: IWizardSection = {
      id: "remarks",
      title: copy.remarks.title,
      explanation: copy.remarks.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group controlId="remarks">
                <Form.Label>{copy.remarks.fields.remarks.label}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={(form.remarks as string) ?? ""}
                  onChange={(e) => setForm({ remarks: e.target.value })}
                  placeholder={copy.remarks.fields.remarks.placeholder}
                  isValid={hasVal(form.remarks)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    return [personal, business, requirements, vehicles, coverage, remarks];
  }, [copy]);

  const initialForm = demoMode ? DEMO_DATA : initialBase;

  const handleSubmit = async (payload: FormShape) => {
    setSubmitError(null);
    setSubmitting(true);

    const enabledIds = sections.filter(s => s.enabled !== false).map(s => s.id);
    const raw = overallCompletion(enabledIds, payload);
    const pctToSend = clampPct(raw);

    // Strings-only payload (align with your backend)
    const customer_DATA = {
      // Meta
      productMode: "contractor",
      submittedAtIso: new Date().toISOString(),
      source: `Contractor Quote Wizard — ${VERSION}`,

      // Contact
      contactFirst: S(payload.contactFirst),
      contactLast: S(payload.contactLast),
      email: S(payload.email),
      phone: S(payload.phone),

      // Business
      businessName: S(payload.businessName),
      fein: S(payload.fein),
      yearsInBusiness: S(payload.yearsInBusiness),
      employeeCount: S(payload.employeeCount),
      tradeType: S(payload.tradeType),

      // Requirements
      needRequirements: S(payload.needRequirements),
      additionalInsured: S(payload.additionalInsured),
      waiverOfSubrogation: S(payload.waiverOfSubrogation),
      limitsPerOccurrence: S(payload.limitsPerOccurrence),
      limitsAggregate: S(payload.limitsAggregate),
      certHolders: S(payload.certHolders),

      // Vehicles
      ownFleet: S(payload.ownFleet),
      vehicleCount: S(payload.vehicleCount),
      primaryVehicleType: S(payload.primaryVehicleType),
      radiusMiles: S(payload.radiusMiles),

      // Coverage
      coverageType: S(payload.coverageType),
      glDeductible: S(payload.glDeductible),

      // Remarks
      remarks: S(payload.remarks),

      // Progress (string pre-submit)
      formCompletionPercent: String(pctToSend),

      // Legacy/meta
      domainName: String(window.location.hostname),
      itemCategory: String("Customer.contractor.quote"),
      itemClass: String("quote.request"),
      itemType: String("legacy.contractor"),
      clientVersion: String(VERSION + ".contractor"),
    };

    if (TEST_MODE) {
      console.log("TEST MODE — customer_DATA:", customer_DATA);
      setSubmitting(false);
      return;
    }

    try {
      const resp = await fetch(API_GATEWAY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(customer_DATA),
      });
      if (!resp.ok) throw new Error("network");
      setSubmitted(true);
    } catch {
      setSubmitError("Error: network error, try again in a minute.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="container my-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Card>
              <Card.Header className="fw-bold">{copy.submitThankYou.header}</Card.Header>
              <Card.Body>
                <p className="mb-0">{copy.submitThankYou.body}</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="container my-4">
      {/* Page Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h3 className="m-0">
          {copy.appTitle} {SHOW_VERSION ? `— ${VERSION}` : ""}
        </h3>

        <div className="d-flex align-items-center gap-3">
          {/* DEMO MODE UI (optional) */}
          {DEMO_MODE_UI_ENABLED && (
            <Form.Check
              type="checkbox"
              id="demoModeToggle"
              label={copy.buttons.demoToggle}
              checked={demoMode}
              onChange={(e) => setDemoMode(e.target.checked)}
              disabled={submitting}
            />
          )}

          {/* View switches */}
          <div className="d-flex gap-2">
            <Button
              variant={uiMode === "wizard" ? "primary" : "outline-primary"}
              onClick={() => setUiMode("wizard")}
              disabled={submitting}
            >
              {copy.buttons.wizardView}
            </Button>
            <Button
              variant={uiMode === "vertical" ? "primary" : "outline-primary"}
              onClick={() => setUiMode("vertical")}
              disabled={submitting}
            >
              {copy.buttons.verticalView}
            </Button>
          </div>
        </div>
      </div>

      {submitError && <Alert variant="danger" className="mb-3">{submitError}</Alert>}

      <DualViewWizard_Contractor_H2
        key={demoMode ? "demo-on" : "demo-off"}
        mode={uiMode}
        sections={sections}
        initialForm={demoMode ? DEMO_DATA : initialBase}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitted={false}
        copy={copy}
      />
    </div>
  );
};

export default Contractor_QuoteWizard_H2_A;
